import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { doc, getDoc, updateDoc, serverTimestamp, onSnapshot } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import { Booking, Space } from '@/types'
import toast from 'react-hot-toast'
import ReviewForm from '@/components/reviews/ReviewForm'
import { createPaymentPreference } from '@/services/paymentService'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'
import { Calendar, Clock, MapPin, ChevronLeft, Info, CreditCard, AlertCircle, CheckCircle2, Star, XCircle } from 'lucide-react'

export default function BookingDetailsPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { currentOrg, user } = useAuthStore()
    const [booking, setBooking] = useState<Booking | null>(null)
    const [space, setSpace] = useState<Space | null>(null)
    const [loading, setLoading] = useState(true)
    const [creatingPreference, setCreatingPreference] = useState(false)

    useEffect(() => {
        if (!id) return

        // Listener em tempo real para o booking - atualiza automaticamente quando webhook processar
        const unsubscribe = onSnapshot(
            doc(db, 'bookings', id),
            async (bookingDoc) => {
                if (bookingDoc.exists()) {
                    const bookingData = { id: bookingDoc.id, ...bookingDoc.data() } as Booking
                    setBooking(bookingData)

                    // Carregar espaço se ainda não foi carregado
                    if (!space || space.id !== bookingData.spaceId) {
                        const spaceDoc = await getDoc(doc(db, 'spaces', bookingData.spaceId))
                        if (spaceDoc.exists()) {
                            setSpace({ id: spaceDoc.id, ...spaceDoc.data() } as Space)
                        }
                    }
                    setLoading(false)
                } else {
                    toast.error('Reserva não encontrada.')
                    navigate('/bookings')
                }
            },
            (error) => {
                console.error('Erro ao escutar reserva:', error)
                toast.error('Erro ao carregar detalhes.')
                setLoading(false)
            }
        )

        return () => unsubscribe()
    }, [id])

    const handleCancelBooking = async () => {
        if (!id) return
        const confirm = window.confirm('Deseja realmente cancelar esta reserva?')
        if (!confirm) return

        try {
            await updateDoc(doc(db, 'bookings', id), {
                status: 'CANCELED',
                updatedAt: serverTimestamp()
            })
            setBooking(prev => prev ? { ...prev, status: 'CANCELED' } as any : null)
            toast.success('Reserva cancelada!')
        } catch (error: any) {
            toast.error('Erro ao cancelar reserva.')
        }
    }

    if (loading) return <div className="py-20 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div></div>
    if (!booking || !space) return null

    const startDate = booking.start?.toDate?.() || new Date()
    const endDate = booking.end?.toDate?.() || new Date()

    return (
        <div className="max-w-4xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <button
                onClick={() => navigate('/bookings')}
                className="flex items-center text-gray-400 hover:text-gray-900 font-black uppercase text-[10px] tracking-widest transition-colors"
            >
                <ChevronLeft className="w-4 h-4 mr-2" /> Voltar para Minhas Reservas
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white rounded-[2.5rem] border border-gray-50 overflow-hidden shadow-xl shadow-gray-100/50">
                        <div className="aspect-video bg-gray-100 relative">
                            {space.photos?.[0] ? (
                                <img src={space.photos[0]} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-200">
                                    <Calendar className="w-16 h-16" />
                                </div>
                            )}
                            <div className="absolute top-6 left-6">
                                <span className={`px - 4 py - 2 rounded - xl text - [10px] font - black uppercase tracking - widest bg - white shadow - xl ${booking.status === 'CONFIRMED' ? 'text-green-600' :
                                    booking.status === 'CANCELED' ? 'text-red-600' : 'text-primary-600'
                                    } `}>
                                    {booking.status}
                                </span>
                            </div>
                        </div>

                        <div className="p-10 space-y-6">
                            <h1 className="text-3xl font-black text-gray-900 uppercase tracking-tight">{space.name}</h1>

                            <div className="grid grid-cols-2 gap-8 pt-4 border-t border-gray-50">
                                <div className="space-y-1">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Início</p>
                                    <div className="flex items-center space-x-2 text-gray-900 font-bold">
                                        <Calendar className="w-4 h-4 text-primary-600" />
                                        <span>{startDate.toLocaleDateString()} às {startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fim</p>
                                    <div className="flex items-center space-x-2 text-gray-900 font-bold">
                                        <Clock className="w-4 h-4 text-primary-600" />
                                        <span>{endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center space-x-2 pt-4">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-500 font-medium">Localizado na organização {currentOrg?.name}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50/50 p-8 rounded-[2rem] border border-blue-100 flex items-start space-x-4">
                        <Info className="w-6 h-6 text-blue-600 shrink-0" />
                        <div className="space-y-2">
                            <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest">Informações Importantes</h4>
                            <p className="text-xs text-blue-800 font-medium leading-relaxed">
                                O cancelamento gratuito é permitido até 24h antes do início da reserva.
                                Caso tenha problemas no acesso ao espaço, entre em contato com a administração.
                            </p>
                        </div>
                    </div>

                    {/* Review Section */}
                    {booking.status === 'COMPLETED' && !booking.reviewed && space && user && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <ReviewForm
                                spaceId={space.id}
                                userId={user.uid}
                                userName={user.displayName || 'Usuário'}
                                bookingId={booking.id}
                                onReviewSubmitted={() => {
                                    setBooking(prev => prev ? { ...prev, reviewed: true } : null)
                                }}
                            />
                        </div>
                    )}

                    {booking.status === 'COMPLETED' && booking.reviewed && (
                        <div className="bg-yellow-50 p-8 rounded-[2rem] border border-yellow-100 text-center space-y-3">
                            <Star className="w-12 h-12 text-yellow-500 mx-auto fill-yellow-500" />
                            <div>
                                <h3 className="text-lg font-black text-yellow-900 uppercase italic tracking-tight">Avaliação Enviada!</h3>
                                <p className="text-xs font-bold text-yellow-700 mt-1 uppercase tracking-widest">Obrigado pelo seu feedback.</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    {/* Status de Pagamento */}
                    <div className="bg-white rounded-[2.5rem] border border-gray-50 p-8 shadow-xl shadow-gray-100/50 space-y-6">
                        <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Pagamento</h3>

                        <span className="text-2xl font-black text-gray-900">
                            {booking.totalPrice === 0 ? 'GRATUITO' : `R$ ${booking.totalPrice?.toFixed(2)} `}
                        </span>
                    </div>

                    {/* Payment Audit Details */}
                    {booking.payment && (booking.payment.status === 'paid' || booking.payment.status === 'approved') && (
                        <div className="bg-gray-50 rounded-xl p-4 text-xs space-y-2 border border-gray-100">
                            <p className="font-bold text-gray-400 uppercase tracking-widest text-[9px] mb-2">Detalhes da Transação</p>
                            <div className="flex justify-between">
                                <span className="text-gray-500">Método:</span>
                                <span className="font-bold text-gray-700 capitalize">
                                    {booking.payment.provider === 'mercadopago' ? 'Mercado Pago' :
                                        booking.payment.provider === 'infinitypay' ? 'InfinityPay' : 'Manual/Pix'}
                                </span>
                            </div>
                            {(booking.payment.mercadoPagoPaymentId || booking.payment.infinityPayInvoiceId) && (
                                <div className="flex justify-between items-center group relative">
                                    <span className="text-gray-500">ID Transação:</span>
                                    <span className="font-mono font-bold text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200 select-all" title="Clique para copiar">
                                        {booking.payment.mercadoPagoPaymentId || booking.payment.infinityPayInvoiceId}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-500">Data:</span>
                                <span className="font-bold text-gray-700">
                                    {booking.updatedAt?.toDate().toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    )}

                    {booking.status === 'CANCELED' && (
                        <div className="text-center p-6 bg-red-50 rounded-3xl border border-red-100 space-y-2">
                            <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
                            <p className="text-xs font-black text-red-800 uppercase tracking-widest">Reserva Cancelada</p>
                            <p className="text-[10px] text-red-700 font-medium">Esta reserva foi cancelada e não pode mais ser processada.</p>
                        </div>
                    )}

                    {booking.status === 'PENDING_APPROVAL' && (
                        <div className="text-center p-6 bg-orange-50 rounded-3xl border border-orange-100 space-y-2">
                            <AlertCircle className="w-8 h-8 text-orange-500 mx-auto" />
                            {booking.payment?.status === 'paid' ? (
                                <>
                                    <p className="text-xs font-black text-green-800 uppercase tracking-widest">✅ PAGO - AGUARDANDO VALIDAÇÃO</p>
                                    <p className="text-[10px] text-orange-700 font-medium">Pagamento confirmado! Aguardando aprovação do administrador.</p>
                                </>
                            ) : (
                                <>
                                    <p className="text-xs font-black text-orange-800 uppercase tracking-widest">Atenção: Validação Pendente</p>
                                    <p className="text-[10px] text-orange-700 font-medium">Sua reserva aguarda validação ou pagamento.</p>
                                </>
                            )}
                        </div>
                    )}

                    {booking.status === 'CONFIRMED' && booking.payment?.status === 'approved' && (
                        <div className="text-center p-6 bg-green-50 rounded-3xl border border-green-100 space-y-2">
                            <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
                            <p className="text-xs font-black text-green-800 uppercase tracking-widest">✅ RESERVA CONFIRMADA</p>
                            <p className="text-[10px] text-green-700 font-medium">Pagamento e reserva confirmados! Tudo pronto.</p>
                        </div>
                    )}

                    {/* Exibir Tarja Vermelha se estiver Aguardando Pagamento explicitamente */}
                    {booking.status === 'PENDING_PAYMENT' && (
                        <div className="text-center p-6 bg-red-50 rounded-3xl border border-red-100 space-y-2 mb-4">
                            <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
                            <p className="text-xs font-black text-red-800 uppercase tracking-widest">Pagamento Pendente</p>
                            <p className="text-[10px] text-red-700 font-medium">Realize o pagamento para confirmar sua reserva.</p>
                        </div>
                    )}

                    {/* Indicador de Processamento de Pagamento InfinityPay */}
                    {booking.payment?.provider === 'infinitypay' &&
                        booking.payment?.status === 'pending' &&
                        booking.status !== 'CANCELED' && (
                            <div className="text-center p-6 bg-blue-50 rounded-3xl border border-blue-100 space-y-3 animate-pulse">
                                <div className="flex items-center justify-center space-x-2">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                    <CreditCard className="w-6 h-6 text-blue-500" />
                                </div>
                                <p className="text-xs font-black text-blue-800 uppercase tracking-widest">Processando Pagamento</p>
                                <p className="text-[10px] text-blue-700 font-medium">
                                    Estamos aguardando o retorno do banco. Por favor, aguarde alguns instantes...
                                </p>
                                <p className="text-[9px] text-blue-600 font-medium italic">
                                    Não feche esta página até a confirmação.
                                </p>
                            </div>
                        )}


                    {(currentOrg?.paymentConfig?.provider === 'mercadopago' || currentOrg?.paymentConfig?.mercadoPago?.enabled) && booking.payment?.status !== 'approved' && booking.payment?.status !== 'paid' && booking.status !== 'CANCELED' && booking.totalPrice > 0 && (
                        <button
                            onClick={async () => {
                                if (!booking || !space) return
                                setCreatingPreference(true)
                                try {
                                    const result = await createPaymentPreference(booking.id, booking.orgId)
                                    if (result?.initPoint) {
                                        window.location.href = result.initPoint
                                    } else {
                                        toast.error('Erro ao gerar link de pagamento')
                                    }
                                } catch (error) {
                                    console.error(error)
                                    toast.error('Erro ao iniciar pagamento')
                                } finally {
                                    setCreatingPreference(false)
                                }
                            }}
                            disabled={creatingPreference}
                            className="w-full py-4 bg-[#d4a94d] text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-[#001c28] transition-all duration-300 shadow-xl shadow-[#d4a94d]/20 hover:shadow-[#001c28]/20 disabled:opacity-50 flex flex-col items-center justify-center gap-2 group"
                        >
                            {creatingPreference ? (
                                <div className="animate-spin h-6 w-6 border-2 border-white rounded-full border-t-transparent" />
                            ) : (
                                <>
                                    <CreditCard className="w-6 h-6 group-hover:scale-110 transition-transform mb-1" />
                                    <span>Realizar Pagamento</span>
                                </>
                            )}
                        </button>
                    )}

                    {currentOrg?.paymentConfig?.provider === 'infinitypay' && currentOrg.paymentConfig?.infinityPay?.enabled && booking.payment?.status !== 'pending' && booking.status !== 'CANCELED' && booking.totalPrice > 0 && (
                        <button
                            onClick={async () => {
                                if (!booking || !space) return
                                setCreatingPreference(true)
                                try {
                                    const createPayment = httpsCallable(functions, 'createInfinityPayPayment')
                                    const result = await createPayment({
                                        bookingId: booking.id,
                                        orgId: currentOrg.id
                                    })
                                    const data = result.data as { success: boolean; paymentLink: string }

                                    if (data.success && data.paymentLink) {
                                        toast.success('Redirecionando para InfinityPay...')
                                        window.location.href = data.paymentLink
                                    } else {
                                        toast.error('Erro ao gerar link de pagamento')
                                    }
                                } catch (error: any) {
                                    console.error(error)
                                    toast.error('Erro ao iniciar pagamento InfinityPay')
                                } finally {
                                    setCreatingPreference(false)
                                }
                            }}
                            disabled={creatingPreference}
                            className="w-full py-4 bg-green-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-green-700 transition-all shadow-xl shadow-green-200 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {creatingPreference ? <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent" /> : <CreditCard className="w-4 h-4" />}
                            Pagar com InfinityPay
                        </button>
                    )}



                    {booking.status === 'CONFIRMED' && (
                        <div className="space-y-4">
                            <div className="text-center p-8 bg-green-50 rounded-[2rem] border border-green-100 space-y-3">
                                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                                <div>
                                    <p className="text-xs font-black text-green-900 uppercase tracking-widest">Reserva Confirmada</p>
                                    <p className="text-[10px] text-green-700 font-medium mt-1">Tudo certo! Divirta-se no seu espaço.</p>
                                </div>
                            </div>

                            <button
                                onClick={async () => {
                                    if (!id) return
                                    try {
                                        await updateDoc(doc(db, 'bookings', id), {
                                            status: 'IN_PROGRESS',
                                            updatedAt: serverTimestamp()
                                        })
                                        setBooking(prev => prev ? { ...prev, status: 'IN_PROGRESS' } as any : null)
                                        toast.success('Check-in realizado! Bom uso.')
                                    } catch (error) {
                                        toast.error('Erro ao fazer check-in.')
                                    }
                                }}
                                className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-200"
                            >
                                <Clock className="w-4 h-4" />
                                <span>Fazer Check-in</span>
                            </button>
                        </div>
                    )}

                    {booking.status === 'IN_PROGRESS' && (
                        <div className="space-y-4">
                            <div className="text-center p-8 bg-blue-50 rounded-[2rem] border border-blue-100 space-y-3 animate-pulse">
                                <Clock className="w-12 h-12 text-blue-500 mx-auto" />
                                <div>
                                    <p className="text-xs font-black text-blue-900 uppercase tracking-widest">Em Uso</p>
                                    <p className="text-[10px] text-blue-700 font-medium mt-1">Você está utilizando este espaço agora.</p>
                                </div>
                            </div>

                            <button
                                onClick={async () => {
                                    if (!id || !booking) return
                                    const confirm = window.confirm('Deseja finalizar o uso agora? O horário restante será liberado.')
                                    if (!confirm) return

                                    try {
                                        const now = new Date()
                                        // Se terminar antes do fim previsto, atualiza o fim para agora
                                        // para liberar o horário.
                                        let updateData: any = {
                                            status: 'COMPLETED',
                                            updatedAt: serverTimestamp()
                                        }

                                        const bookingEnd = booking.end?.toDate?.() || new Date()
                                        if (now < bookingEnd) {
                                            updateData.end = now // Libera o horário
                                        }

                                        await updateDoc(doc(db, 'bookings', id), updateData)

                                        setBooking(prev => prev ? { ...prev, status: 'COMPLETED' } as any : null)
                                        toast.success('Check-out realizado com sucesso!')
                                    } catch (error) {
                                        toast.error('Erro ao fazer check-out.')
                                    }
                                }}
                                className="w-full flex items-center justify-center space-x-2 bg-green-600 text-white py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-green-700 transition-all shadow-xl shadow-green-200"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                <span>Fazer Check-out</span>
                            </button>
                        </div>
                    )}

                    {booking.status !== 'CANCELED' && booking.status !== 'COMPLETED' && booking.status !== 'IN_PROGRESS' && (
                        <div className="pt-6 border-t border-gray-50 space-y-3">
                            {(useAuthStore.getState().userRole === 'owner' || useAuthStore.getState().userRole === 'admin') && (
                                <button
                                    onClick={async () => {
                                        if (!window.confirm('Confirmar aprovação manual deste pagamento?')) return;
                                        try {
                                            await updateDoc(doc(db, 'bookings', id!), {
                                                status: 'CONFIRMED',
                                                'payment.status': 'approved',
                                                'payment.method': 'manual_admin',
                                                updatedAt: serverTimestamp()
                                            })
                                            setBooking(prev => prev ? { ...prev, status: 'CONFIRMED' } as any : null)
                                            toast.success('Pagamento aprovado manualmente!')
                                        } catch (e) {
                                            console.error(e)
                                            toast.error('Erro ao aprovar.')
                                        }
                                    }}
                                    className="w-full flex items-center justify-center space-x-2 text-green-600 font-black uppercase text-[10px] tracking-widest hover:text-white hover:bg-green-600 py-4 rounded-2xl border-2 border-green-50 transition-all group"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span>Aprovar Pagamento (Admin)</span>
                                </button>
                            )}

                            <button
                                onClick={handleCancelBooking}
                                className="w-full flex items-center justify-center space-x-2 text-red-500 font-black uppercase text-[10px] tracking-widest hover:text-white hover:bg-red-500 py-4 rounded-2xl border-2 border-red-50 transition-all group"
                            >
                                <XCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                <span>Cancelar Reserva</span>
                            </button>
                            <p className="text-[9px] text-gray-400 text-center mt-3 font-medium uppercase tracking-[0.05em]">Lembre-se: O cancelamento é irreversível.</p>
                        </div>
                    )}
                </div>
            </div>
        </div >
    )
}
